import { useTheme } from '@emotion/react';
import { useState, useEffect } from 'react';

interface WindowSize {
  width: number;
  height: number;
}

export function useWindowSize() {
  if (typeof window === 'undefined') return { width: 0, height: 0 };

  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}

/**
 * return the current breakpoint (index and value) based on the current screen size
 * @returns an Array with the index of the current breakpoint and the corresponding value (e.g: [2, '768px'] or [2, 768] or [1, '40em'])
 */
export function useCurrentBreakpoint(): [number, number | string] {
  let { breakpoints } = useTheme();
  const { width } = useWindowSize();

  if (!breakpoints) breakpoints = ['40em', '52em', '64em'];

  const breakpointsInPx = breakpoints.map((x) => {
    if (typeof x === 'number') {
      return x;
    } else if (x.includes('px')) {
      return parseInt(x);
    } else if (x.includes('em') || x.includes('rem')) {
      return parseInt(x) * 16;
    } else {
      return parseInt(x);
    }
  });

  let currentBreakpoint = 0;
  for (let i = 0; i < breakpointsInPx.length - 1; i++) {
    if (width > breakpointsInPx[i]) currentBreakpoint = i + 1;
  }

  return [currentBreakpoint, breakpoints[currentBreakpoint]];
}
